import { useToastStore } from "@/stores/toast.store";
import { ref } from "vue";
import { useI18n } from "vue-i18n";

declare global {
  interface Window {
    Stripe: any;
  }
}

export function loadStripeScript(): Promise<void> {
  if (typeof window.Stripe === "function") return Promise.resolve();

  return new Promise((resolve, reject) => {
    // Add script if not already present (index.html may have added it)
    if (!document.querySelector('script[src*="js.stripe.com"]')) {
      const script = document.createElement("script");
      script.src = "https://js.stripe.com/v3/";
      script.async = true;
      script.onerror = () => reject(new Error("Failed to load Stripe.js"));
      document.head.appendChild(script);
    }

    // Poll until Stripe is available (handles both fresh load and already-loading script)
    const interval = setInterval(() => {
      if (typeof window.Stripe === "function") {
        clearInterval(interval);
        resolve();
      }
    }, 50);

    setTimeout(() => {
      clearInterval(interval);
      reject(new Error("Stripe.js failed to load within 10s"));
    }, 10000);
  });
}

export const useStripe = () => {
  const toastStore = useToastStore();
  const { t } = useI18n();

  const confirmPaymentRef = ref<() => Promise<string | null>>(async () => null);
  const hasInitialized = ref(false);

  async function init(
    publishableKey: string,
    clientSecret: string,
    paymentElementId: string,
  ) {
    if (hasInitialized.value) return;

    await loadStripeScript();
    const stripe = window.Stripe(publishableKey);

    const styles = getComputedStyle(document.body);

    const options = {
      clientSecret: clientSecret,
      // Fully customizable with appearance API.
      appearance: {
        theme: "flat",
        variables: {
          colorPrimary: styles.getPropertyValue("--brand-primary"),
          colorBackground: styles.getPropertyValue("--brand-bg-main"),
          colorText: styles.getPropertyValue("--basics-primary"),
          colorTextPlaceholder: styles.getPropertyValue("--basics-tertiary-60"),
          colorDanger: styles.getPropertyValue("--basics-accent-red"),
          fontFamily: "Ideal Sans, system-ui, sans-serif",
          spacingUnit: "8px",
          borderRadius: "8px",
        },
        rules: {
          ".Input": {
            border: "1px solid var(--colorPrimary)",
            padding: "12px 16px",
          },
          ".Tab": {
            border: "1px solid var(--colorPrimary)",
            padding: "8px 24px",
          },
          ".Tab--selected": {
            border: "1px solid var(--colorPrimary)",
          },
        },
      },
      paymentMethodOrder: [
        "apple_pay",
        "google_pay",
        "card",
        "paypal",
        "amazon_pay",
        "cashapp",
      ],
    };

    // Set up Stripe.js and Elements to use in checkout form, passing the client secret obtained in step 5
    const elements = stripe.elements(options);

    // Create and mount the Payment Element
    const paymentElement = elements.create("payment");

    paymentElement.mount(paymentElementId);

    hasInitialized.value = true;
    confirmPaymentRef.value = async () => {
      const { paymentIntent, error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
      });

      if (error) {
        console.error(error);
        toastStore.addToast({
          message: t("ui.payment.subscriptionPurchaseError"),
          type: "error",
        });
        return null;
      }
      return paymentIntent.id;
    };
  }

  return {
    hasInitialized,
    init,
    confirmPaymentRef,
  };
};
